import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, CircularProgress } from '@mui/material';
import type { BillingTerm, SubscriptionStatus, InvoiceScheduleLineItemDetail } from '@techgitter/contracts';

import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import { SmartSelect, DateField } from '../dropdown';
import { ProductMappingDialog } from '../reconciliation/ProductMappingDialog';
import { getProductMappings } from '../../services/productMappingService';
import { fetchInventory } from '../../services/inventoryApi';
import { getInvoiceSchedules, getInvoiceSchedule } from '../../services/invoiceScheduleAdminService';
import { getCustomerIndexCached } from '../../services/customerIndexCacheService';
import {
  useCreateSubscriptionAndBill,
  type CreateSubAndBillResult,
  type BillingPlan,
} from './useCreateSubscriptionAndBill';
import './CreateSubscriptionAndBillPanel.css';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'cancelled', label: 'Cancelled' },
];
const CREATE_SENTINEL = '__create__';

interface Props {
  open: boolean;
  customer: { dataverseId: string; name?: string };
  vendorProduct: {
    id: string;
    name?: string;
    sku?: string;
    vendorId: string;
    billingTerm?: BillingTerm | null;
  };
  /** Vendor-reported seat count (prefills the subscription seats). */
  defaultSeats: number;
  onClose: () => void;
  /** Fired after a successful (or partial) create+bill so callers can react —
   *  e.g. the reconciliation page records the variance resolution. */
  onCompleted?: (result: CreateSubAndBillResult) => void;
}

interface Commercial {
  productDvId: string;
  rsProductId: number;
  sku: string;
  name: string;
  retail: number;
  seatMultiplier: number;
}

interface ScheduleOption {
  scheduleId: string;
  name: string;
  lineItemCount: number;
}

/**
 * Reusable "Create subscription & bill" panel: creates the customer-vendor
 * subscription (commitment) and adds the mapped commercial product to the
 * customer's recurring schedule (creating one if none) — the fix for a
 * no-subscription / coverage reconciliation variance. Decoupled from
 * reconciliation: pass plain inputs + an onCompleted hook.
 *
 * Requires the vendor product to be mapped to a commercial product (the billing
 * line + its price come from the catalog); blocks with a "Create mapping"
 * action when it isn't.
 */
export const CreateSubscriptionAndBillPanel: React.FC<Props> = ({
  open,
  customer,
  vendorProduct,
  defaultSeats,
  onClose,
  onCompleted,
}) => {
  const term: BillingTerm = vendorProduct.billingTerm ?? 'monthly';
  const frequency: 'Monthly' | 'Yearly' = term === 'annual' ? 'Yearly' : 'Monthly';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [unmapped, setUnmapped] = useState(false);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);

  const [commercial, setCommercial] = useState<Commercial | null>(null);
  const [schedules, setSchedules] = useState<ScheduleOption[]>([]);
  const [scheduleSel, setScheduleSel] = useState<string>(CREATE_SENTINEL);
  const [repairShoprId, setRepairShoprId] = useState<string | null>(null);

  const [seats, setSeats] = useState<number>(defaultSeats);
  const [status, setStatus] = useState<SubscriptionStatus>('active');
  const [renewalDate, setRenewalDate] = useState<string>('');
  const [label, setLabel] = useState<string>('');
  // Existing lines on the selected schedule — drives the coverage-state-aware
  // billing action (Fix 2), so we never blindly append a duplicate.
  const [scheduleLines, setScheduleLines] = useState<InvoiceScheduleLineItemDetail[] | null>(null);
  const [billingAction, setBillingAction] = useState<'none' | 'increment' | 'add'>('add');

  const { phase, error: runError, run } = useCreateSubscriptionAndBill();
  const saving = phase === 'submitting';

  const load = React.useCallback(
    async (signal: AbortSignal) => {
      setLoading(true);
      setLoadError(null);
      setUnmapped(false);
      try {
        const mappings = await getProductMappings(
          { vendorProductId: vendorProduct.id, includeInactive: false },
          signal,
        );
        const mapping = mappings[0];
        if (!mapping) {
          setUnmapped(true);
          return;
        }

        // Resolve the commercial product's RS id + retail price from the catalog.
        const inv = await fetchInventory({
          search: mapping.productSku ?? '',
          page: 1,
          pageSize: 50,
          signal,
        });
        const product = inv.items.find((p) => p.id === mapping.productId);
        if (!product) {
          setLoadError('Could not load the mapped commercial product to price the line.');
          return;
        }
        setCommercial({
          productDvId: mapping.productId,
          rsProductId: Number(product.productId),
          sku: product.sku,
          name: product.displayName || product.sku,
          retail: product.unitCost ?? 0,
          seatMultiplier: mapping.seatMultiplier || 1,
        });

        // Customer's schedules of the target frequency (auto-pick single, else prompt).
        const all = await getInvoiceSchedules(signal);
        const matches = all
          .filter((s) => s.customerId === customer.dataverseId && s.frequency === frequency)
          .map((s) => ({ scheduleId: s.scheduleId, name: s.name, lineItemCount: s.lineItemCount }));
        setSchedules(matches);
        setScheduleSel(matches.length === 1 ? matches[0].scheduleId : matches.length >= 2 ? matches[0].scheduleId : CREATE_SENTINEL);

        // RepairShopr customer id (needed only when creating a schedule).
        try {
          const idx = await getCustomerIndexCached();
          const found = idx.items.find((c) => c.dataverseCustomerId === customer.dataverseId);
          setRepairShoprId(found?.repairShoprCustomerId ?? null);
        } catch {
          setRepairShoprId(null);
        }
      } catch (e) {
        if (signal.aborted) return;
        setLoadError(e instanceof Error ? e.message : 'Failed to load mapping/pricing.');
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [vendorProduct.id, customer.dataverseId, frequency],
  );

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [open, load]);

  const qty = useMemo(() => {
    const mult = commercial?.seatMultiplier || 1;
    return Math.max(1, Math.ceil((Number(seats) || 0) / mult));
  }, [seats, commercial]);

  const creatingSchedule = scheduleSel === CREATE_SENTINEL;

  // Load the selected schedule's lines so we can see what it already bills.
  useEffect(() => {
    if (!open || creatingSchedule || !scheduleSel || scheduleSel === CREATE_SENTINEL) {
      setScheduleLines(null);
      return;
    }
    const controller = new AbortController();
    getInvoiceSchedule(scheduleSel, controller.signal)
      .then((detail) => setScheduleLines(detail.lineItems ?? []))
      .catch(() => {
        if (!controller.signal.aborted) setScheduleLines([]);
      });
    return () => controller.abort();
  }, [open, scheduleSel, creatingSchedule]);

  // Lines already billing this SKU on the selected schedule.
  const matchingLines = useMemo(
    () =>
      commercial && scheduleLines
        ? scheduleLines.filter((l) => l.name === commercial.sku)
        : [],
    [commercial, scheduleLines],
  );
  const existingBilledQty = useMemo(
    () => matchingLines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0),
    [matchingLines],
  );
  // Increment can only target one line unambiguously.
  const singleMatch = matchingLines.length === 1 ? matchingLines[0] : undefined;

  // Recommend the safe billing action from the detected coverage state, and
  // follow the inputs as they change (the operator can still override).
  useEffect(() => {
    if (creatingSchedule || scheduleLines === null) return;
    if (existingBilledQty === 0) setBillingAction('add');
    else if (existingBilledQty >= qty) setBillingAction('none');
    else setBillingAction(singleMatch ? 'increment' : 'none');
  }, [creatingSchedule, scheduleLines, existingBilledQty, qty, singleMatch]);

  const billingActionOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    if (existingBilledQty > 0) {
      opts.push({
        value: 'none',
        label: `Commitment only — leave billing unchanged (already ${existingBilledQty})`,
      });
      if (existingBilledQty < qty && singleMatch) {
        opts.push({
          value: 'increment',
          label: `Increment existing line ${existingBilledQty} → ${qty} (+${qty - existingBilledQty})`,
        });
      }
      opts.push({ value: 'add', label: `Add a second line (${qty}) — duplicates the SKU` });
    } else {
      opts.push({ value: 'add', label: `Add billing line (${qty})` });
      opts.push({ value: 'none', label: 'Commitment only — do not bill' });
    }
    return opts;
  }, [existingBilledQty, qty, singleMatch]);

  const isValid =
    !saving &&
    !!commercial &&
    Number.isFinite(seats) &&
    seats > 0 &&
    (!creatingSchedule || !!repairShoprId);

  const scheduleOptions = useMemo(
    () => [
      ...schedules.map((s) => ({
        value: s.scheduleId,
        label: `${s.name} (${s.lineItemCount} lines)`,
      })),
      { value: CREATE_SENTINEL, label: `Create new ${frequency} schedule` },
    ],
    [schedules, frequency],
  );

  const submit = async () => {
    if (!commercial) return;
    const line = {
      name: commercial.sku || commercial.name,
      quantity: qty,
      priceRetail: commercial.retail,
      productId: commercial.rsProductId,
      recurringTypeId: 1,
      oneTimeCharge: false,
    };

    let billing: BillingPlan;
    if (creatingSchedule) {
      billing = {
        mode: 'createSchedule',
        createSchedule: {
          customer: { dataverseId: customer.dataverseId, repairShoprId: repairShoprId ?? '' },
          // Schedule names follow the reconciliation billing convention so RS/DV
          // schedules read consistently: annual vs monthly + the business name.
          name:
            term === 'annual'
              ? `Annual Subscriptions - ${customer.name ?? 'Customer'}`
              : `Monthly Subscriptions and Support Services Rendered - ${customer.name ?? 'Customer'}`,
          frequency,
          // Bundle the recurring line into the orchestrated create (create
          // schedule → add line in one handoff); same recurring fields as the
          // add-line path so it bills correctly.
          lineItems: [
            {
              name: line.name,
              quantity: line.quantity,
              priceRetail: line.priceRetail,
              productId: line.productId,
              recurringTypeId: line.recurringTypeId,
              oneTimeCharge: line.oneTimeCharge,
            },
          ],
        },
      };
    } else if (billingAction === 'none') {
      billing = { mode: 'none' };
    } else if (billingAction === 'increment') {
      billing = {
        mode: 'increment',
        scheduleId: scheduleSel,
        increment: {
          productSku: commercial.sku,
          delta: qty - existingBilledQty,
          repairShoprLineId: singleMatch?.repairShoprLineId,
        },
      };
    } else {
      // add — flag an intentional duplicate when a line for the SKU already exists.
      billing = {
        mode: 'add',
        scheduleId: scheduleSel,
        line: existingBilledQty > 0 ? { ...line, allowDuplicateSku: true } : line,
      };
    }

    const result = await run({
      subscription: {
        customerId: customer.dataverseId,
        vendorProductId: vendorProduct.id,
        vendorId: vendorProduct.vendorId,
        seats: Math.round(seats),
        billingCadence: term,
        status,
        renewalDate: renewalDate || null,
        label: label.trim() || null,
      },
      billing,
    });
    if (result) onCompleted?.(result);
  };

  const fmtAud = (n: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);

  // The SidePanel primary action adapts: "Create mapping" while unmapped, else "Create & bill".
  const primaryLabel = unmapped
    ? 'Create mapping'
    : saving
      ? 'Working…'
      : !creatingSchedule && billingAction === 'none'
        ? 'Create subscription'
        : billingAction === 'increment'
          ? 'Create & adjust billing'
          : 'Create & bill';

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        aria-label="Create subscription and bill"
        PaperProps={{
          sx: { background: 'transparent', boxShadow: 'none', borderRadius: 0, overflow: 'visible' },
        }}
      >
        <SidePanel
          title="Create subscription & bill"
          submitLabel={primaryLabel}
          isValid={unmapped ? true : isValid}
          onClose={() => {
            if (!saving) onClose();
          }}
          onSubmit={unmapped ? () => setMappingDialogOpen(true) : submit}
        >
          {(loadError || runError) && (
            <div className="side-panel__alert side-panel__alert--error">{loadError || runError}</div>
          )}
          {phase === 'partial' && (
            <div className="side-panel__alert side-panel__alert--warning">
              Subscription created, but adding the billing line failed — add it manually on the
              customer's {frequency} schedule.
            </div>
          )}

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
              <CircularProgress size={24} />
            </div>
          ) : unmapped ? (
            <div className="cs-bill__block">
              {vendorProduct.name ?? vendorProduct.sku ?? 'This vendor product'} isn't mapped to a
              commercial product yet. A mapping is required so the billing line and its price come
              from the catalog. Create the mapping, then reopen this action.
            </div>
          ) : (
            commercial && (
              <>
                <p className="cs-bill__hint">
                  Create the subscription for {customer.name ?? 'this customer'} and add{' '}
                  {commercial.name} to their recurring {frequency.toLowerCase()} billing.
                </p>

                <div className="cs-bill__readonly">
                  <span className="cs-bill__readonly-label">Commercial product</span>
                  <span>{commercial.name}</span>
                </div>

                <FormField label="Label">
                  <input
                    type="text"
                    value={label}
                    placeholder="e.g. tenant name — optional"
                    onChange={(e) => setLabel(e.target.value)}
                  />
                </FormField>

                <FormField label="Seats (vendor reported)">
                  <input
                    type="number"
                    min={1}
                    value={seats}
                    onChange={(e) => setSeats(Number(e.target.value))}
                  />
                </FormField>

                <div className="cs-bill__readonly">
                  <span className="cs-bill__readonly-label">Term</span>
                  <span>{term === 'annual' ? 'Annual' : 'Monthly'}</span>
                </div>

                <SmartSelect
                  label="Status"
                  value={status}
                  options={STATUS_OPTIONS}
                  onChange={(v) => {
                    if (v) setStatus(v as SubscriptionStatus);
                  }}
                />

                <DateField
                  label="Renewal date (optional)"
                  value={renewalDate || null}
                  onChange={(iso) => setRenewalDate(iso ?? '')}
                />

                <hr className="cs-bill__divider" />

                <SmartSelect
                  label="Billing schedule"
                  value={scheduleSel}
                  options={scheduleOptions}
                  onChange={(v) => {
                    if (v) setScheduleSel(v);
                  }}
                />
                {creatingSchedule && !repairShoprId && (
                  <span className="cs-bill__help">
                    Can't create a schedule — this customer has no RepairShopr id on file.
                  </span>
                )}
                {creatingSchedule && repairShoprId && (
                  <div className="side-panel__alert side-panel__alert--warning">
                    A new {frequency} schedule will be created. The RS API can't set these, so
                    afterwards edit the schedule in RepairShopr: Last day of month (checked), Keep
                    prices in sync (checked), Billing terms (Standard), Period mode (In advance
                    inclusive).
                  </div>
                )}

                {!creatingSchedule && scheduleLines !== null && (
                  <>
                    <SmartSelect
                      label="Billing action"
                      value={billingAction}
                      options={billingActionOptions}
                      onChange={(v) => {
                        if (v) setBillingAction(v as 'none' | 'increment' | 'add');
                      }}
                    />
                    {existingBilledQty > 0 && (
                      <span className="cs-bill__help">
                        This schedule already bills {existingBilledQty} × {commercial.sku} — the
                        commitment is covered, so adding a line would duplicate it.
                      </span>
                    )}
                  </>
                )}

                <div className="cs-bill__readonly">
                  <span className="cs-bill__readonly-label">Billing</span>
                  <span>
                    {!creatingSchedule && billingAction === 'none'
                      ? 'No change — commitment only'
                      : !creatingSchedule && billingAction === 'increment'
                        ? `${commercial.sku}: ${existingBilledQty} → ${qty}`
                        : `${commercial.name} × ${qty} @ ${fmtAud(commercial.retail)}`}
                  </span>
                </div>
                {commercial.seatMultiplier > 1 && billingAction !== 'none' && (
                  <span className="cs-bill__help">
                    {seats} vendor seats ÷ {commercial.seatMultiplier} = {qty} billed.
                  </span>
                )}
              </>
            )
          )}
        </SidePanel>
      </Dialog>

      {mappingDialogOpen && (
        <ProductMappingDialog
          open
          initialVendorProduct={{
            id: vendorProduct.id,
            name: vendorProduct.name,
            sku: vendorProduct.sku,
            billingTerm: vendorProduct.billingTerm,
            vendorId: vendorProduct.vendorId,
          } as any}
          onClose={() => setMappingDialogOpen(false)}
          onSaved={() => {
            setMappingDialogOpen(false);
            // Re-load now that a mapping exists.
            const controller = new AbortController();
            load(controller.signal);
          }}
        />
      )}
    </>
  );
};
